import { runtimeConfig } from "./runtime-config.js";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Camera, MediaTypeSelection } from "@capacitor/camera";
import { Geolocation } from "@capacitor/geolocation";
import { Haptics } from "@capacitor/haptics";
import { Network } from "@capacitor/network";
import { Preferences } from "@capacitor/preferences";
import { readAppViewport } from "./viewport.js";
import "./mobile-viewport.css";

const isNativePlatform = Capacitor.isNativePlatform();
const nativeGeolocation = isNativePlatform ? Geolocation : null;
const browserGeolocation = navigator.geolocation;
const nativeWatchers = new Map();
let watcherSequence = 0;
const persistentAuthKeys = [
  "vrocs-api-auth-token",
  "vrocs-api-session-user-id",
  "vrocs-api-cookie-session",
  "vrocs-language",
  "vrocs-route-planning-pending-v1",
  "vrocs-journey-interpret-pending-v1"
];

const storage = {
  async get(key) {
    if (!isNativePlatform) return window.localStorage.getItem(key);
    return (await Preferences.get({ key })).value;
  },

  async set(key, value) {
    if (value == null) return this.remove(key);
    if (isNativePlatform) await Preferences.set({ key, value: String(value) });
  },

  async remove(key) {
    if (isNativePlatform) await Preferences.remove({ key });
  }
};

async function restoreNativeSession() {
  if (!isNativePlatform) return;
  await Promise.all(persistentAuthKeys.map(async (key) => {
    const value = await storage.get(key);
    if (value != null && window.localStorage.getItem(key) == null) {
      window.localStorage.setItem(key, value);
    }
  }));
}

await restoreNativeSession();

function normalizePosition(position) {
  if (!position?.coords) return position;
  return {
    coords: {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      altitude: position.coords.altitude ?? null,
      altitudeAccuracy: position.coords.altitudeAccuracy ?? null,
      heading: position.coords.heading ?? null,
      speed: position.coords.speed ?? null
    },
    timestamp: position.timestamp || Date.now()
  };
}

const geolocation = {
  supported: Boolean(nativeGeolocation || browserGeolocation),

  async getPermissionState() {
    if (nativeGeolocation?.checkPermissions) {
      const result = await nativeGeolocation.checkPermissions();
      return result.location || result.coarseLocation || "prompt";
    }
    if (!navigator.permissions?.query) return "unknown";
    try {
      const result = await navigator.permissions.query({ name: "geolocation" });
      return result.state || "unknown";
    } catch (error) {
      return "unknown";
    }
  },

  getCurrentPosition(success, error, options = {}) {
    if (nativeGeolocation) {
      nativeGeolocation.getCurrentPosition(options)
        .then((position) => success(normalizePosition(position)))
        .catch(error);
      return;
    }
    browserGeolocation?.getCurrentPosition(success, error, options);
  },

  watchPosition(success, error, options = {}) {
    if (!nativeGeolocation) {
      return browserGeolocation?.watchPosition(success, error, options);
    }

    const localId = `native-${++watcherSequence}`;
    nativeWatchers.set(localId, { cancelled: false, nativeId: null });
    nativeGeolocation.watchPosition(options, (position, watchError) => {
      if (watchError) {
        error?.(watchError);
        return;
      }
      if (position) success(normalizePosition(position));
    }).then((nativeId) => {
      const watcher = nativeWatchers.get(localId);
      if (!watcher || watcher.cancelled) {
        nativeGeolocation.clearWatch({ id: nativeId });
        return;
      }
      watcher.nativeId = nativeId;
    }).catch(error);
    return localId;
  },

  clearWatch(id) {
    if (!nativeGeolocation) {
      browserGeolocation?.clearWatch(id);
      return;
    }
    const watcher = nativeWatchers.get(id);
    if (!watcher) return;
    watcher.cancelled = true;
    if (watcher.nativeId) nativeGeolocation.clearWatch({ id: watcher.nativeId });
    nativeWatchers.delete(id);
  },

  async requestPermission() {
    if (nativeGeolocation?.requestPermissions) return nativeGeolocation.requestPermissions();
    return { location: await this.getPermissionState() };
  }
};

const media = {
  supported: isNativePlatform,

  async choosePhotos(limit = 8) {
    if (!isNativePlatform) return [];
    const result = await Camera.chooseFromGallery({
      mediaType: MediaTypeSelection.Photo,
      allowMultipleSelection: true,
      limit: Math.max(1, Math.min(8, Number(limit) || 8)),
      quality: 82,
      correctOrientation: true,
      includeMetadata: true
    });
    return Promise.all(result.results.map(async (item, index) => {
      const source = item.webPath || (item.uri ? Capacitor.convertFileSrc(item.uri) : "");
      const response = await fetch(source);
      const blob = await response.blob();
      const format = item.metadata?.format || blob.type.split("/")[1] || "jpeg";
      return new File([blob], `vrocs-photo-${Date.now()}-${index}.${format}`, {
        type: blob.type || `image/${format}`
      });
    }));
  }
};

const network = {
  async getStatus() {
    if (isNativePlatform) return Network.getStatus();
    return { connected: navigator.onLine, connectionType: navigator.onLine ? "unknown" : "none" };
  },

  async addListener(callback) {
    if (isNativePlatform) return Network.addListener("networkStatusChange", callback);
    const update = () => callback({
      connected: navigator.onLine,
      connectionType: navigator.onLine ? "unknown" : "none"
    });
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return {
      remove() {
        window.removeEventListener("online", update);
        window.removeEventListener("offline", update);
      }
    };
  }
};

const haptics = {
  async selection() {
    if (isNativePlatform) await Haptics.selectionChanged();
  },

  async impact() {
    if (isNativePlatform) await Haptics.impact();
  }
};

function appPathFromIncomingUrl(rawUrl) {
  try {
    const url = new URL(rawUrl);
    if (url.protocol === "vrocs:") {
      const route = url.hostname === "auth" ? "/auth-complete.html" : `/${url.hostname}${url.pathname}`;
      return `${route.replace(/\/{2,}/g, "/")}${url.search}${url.hash}`;
    }
    if (["vrocks.me", "www.vrocks.me", "staging.vrocks.me"].includes(url.hostname)) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch (error) {
    return null;
  }
  return null;
}

function openIncomingUrl(rawUrl) {
  const target = appPathFromIncomingUrl(rawUrl);
  if (target) window.location.assign(target);
}

if (isNativePlatform) {
  App.addListener("appUrlOpen", ({ url }) => openIncomingUrl(url));
  App.addListener("appStateChange", ({ isActive }) => {
    window.dispatchEvent(new CustomEvent("vrocs:app-state", { detail: { isActive } }));
  });
  App.addListener("appRestoredResult", (result) => {
    window.dispatchEvent(new CustomEvent("vrocs:app-restored-result", { detail: result }));
  });
  App.getLaunchUrl().then((result) => {
    if (result?.url) openIncomingUrl(result.url);
  }).catch(() => {});
  network.addListener((status) => {
    window.dispatchEvent(new Event(status.connected ? "online" : "offline"));
  }).catch(() => {});
}

function syncViewport() {
  const { height, keyboardInset, keyboardVisible } = readAppViewport();
  document.documentElement.style.setProperty("--app-viewport-height", `${height}px`);
  document.documentElement.style.setProperty("--keyboard-inset", `${keyboardInset}px`);
  document.documentElement.classList.toggle("keyboard-visible", keyboardVisible);
}

const displayMode = isNativePlatform
  ? "native"
  : window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone
    ? "pwa"
    : "browser";

document.documentElement.classList.add(`platform-${displayMode}`);
syncViewport();
window.addEventListener("resize", syncViewport, { passive: true });
window.visualViewport?.addEventListener("resize", syncViewport, { passive: true });
window.visualViewport?.addEventListener("scroll", syncViewport, { passive: true });
document.addEventListener("focusin", syncViewport, { passive: true });
document.addEventListener("focusout", () => requestAnimationFrame(syncViewport), { passive: true });

export const platform = Object.freeze({
  runtime: displayMode,
  isNative: isNativePlatform,
  isInstalled: displayMode === "native" || displayMode === "pwa",
  geolocation,
  media,
  network,
  storage,
  haptics,
  syncViewport
});

window.VROCS_PLATFORM = platform;

document.addEventListener("click", (event) => {
  if (!isNativePlatform) return;
  if (event.target.closest("[data-app-view], .primary-action, [data-map-category]")) {
    haptics.selection().catch(() => {});
  }
}, { passive: true });

document.addEventListener("click", async (event) => {
  if (!isNativePlatform) return;
  const input = event.target.closest('input[type="file"][accept*="image"]');
  if (!input) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  try {
    const files = await media.choosePhotos(input.multiple ? 8 : 1);
    if (!files.length || typeof DataTransfer === "undefined") return;
    const transfer = new DataTransfer();
    files.forEach((file) => transfer.items.add(file));
    input.files = transfer.files;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  } catch (error) {
    window.dispatchEvent(new CustomEvent("vrocs:media-error", { detail: error }));
  }
}, true);
